/**
 * The control plane's figures — the tab that renders on EVERY visit to every
 * project, and the one a PM reads to decide whether anything needs them today.
 *
 * Its sections already refuse correctly when a request FAILS: SectionBody
 * short-circuits on loading, on a disabled capability, and on a classified
 * error, all before a figure is drawn. That was never the defect, and this
 * module does not touch it.
 *
 * The defect is a read that SUCCEEDED and said nothing:
 *
 *   (gates.data?.hardGates || []).length     ->  0, coloured GREEN
 *   materials.data?.conflictCount ?? 0       ->  0, coloured GREEN
 *
 * Green is the claim. "No open hard gates" and "nobody established how many
 * hard gates there are" are different sentences, and only one of them should
 * let a PM close the tab.
 *
 * AND `|| []` IS WORSE THAN A FABRICATED ZERO. Strings have a `.length` too:
 *
 *   ('unavailable' || []).length             ->  11
 *
 * so an unreadable payload does not merely read as empty, it can read as
 * ELEVEN OPEN HARD GATES, in red, on a project that has none. That is F-14 in
 * its sharpest form — the guard does not just fail to protect, it invents.
 *
 * This module adds no vocabulary of its own: the {established, value} fact
 * shape is the command presenter's, because a second copy of an answer is the
 * defect those modules exist to remove.
 */
import { numberFact, NOT_ESTABLISHED, NOT_ESTABLISHED_LABEL } from '../command/commandPresentation';

export { NOT_ESTABLISHED, NOT_ESTABLISHED_LABEL };

/**
 * A count derived from a LIST the backend sent.
 *
 * Established only when the value is genuinely an array. Anything else — a
 * string, an object, a number, absent — is NOT ESTABLISHED, because the only
 * honest count of a list nobody sent is no count at all.
 */
export const listCount = (raw) =>
  Array.isArray(raw) ? { established: true, value: raw.length } : NOT_ESTABLISHED;

/** The rows themselves, safe to map. Never a string, never null. */
export const listRows = (raw) => (Array.isArray(raw) ? raw : []);

/**
 * A count the backend STATES as a number. `?? 0` turned an unstated figure
 * into a stated zero; numberFact keeps them apart, and a real 0 stays a real 0.
 */
export const statedCount = (raw) => numberFact(raw);

/** A figure nobody established never renders as a digit. */
export const factLabel = (fact) =>
  fact && fact.established ? String(fact.value) : NOT_ESTABLISHED_LABEL;

/**
 * A ratio of two facts. Both must be established, because "3 / —" invites the
 * reader to supply the missing half themselves, and "0 / 0" is a completeness
 * claim about a project nobody measured.
 */
export const ratioLabel = (resolved, total) =>
  resolved && resolved.established && total && total.established
    ? `${resolved.value} / ${total.value}`
    : NOT_ESTABLISHED_LABEL;

/**
 * Tone for a figure.
 *
 * An unestablished figure is NEVER coloured as news — not green, not red.
 * Green on an unknown hard-gate count tells a PM the project is clear; red on
 * a fabricated eleven sends them hunting for gates that do not exist. Both are
 * the module's whole reason for existing.
 */
export const factTone = (fact, { zeroIs = 'good', nonZeroIs = 'bad' } = {}) => {
  if (!fact || !fact.established) return 'unknown';
  return fact.value === 0 ? zeroIs : nonZeroIs;
};

/**
 * The detail line under a metric. Only ever drawn from rows that are really
 * rows — a string payload would otherwise be indexed character by character.
 */
export const firstRowDetail = (raw, field) => {
  const rows = listRows(raw);
  const first = rows[0];
  if (!first || typeof first !== 'object') return undefined;
  const value = first[field];
  return typeof value === 'string' && value.length > 0 ? value : undefined;
};
